<script setup>
import { ref, computed, onMounted } from 'vue';
import { Head, Link } from '@inertiajs/inertia-vue3';
import BreezeAuthenticatedLayout from '@/Layouts/Authenticated.vue';
import PremiumPageHeader from '@/Components/PremiumPageHeader.vue';
import { Icon } from '@iconify/vue';
import axios from 'axios';
import * as XLSX from 'xlsx';

import DoughnutChart from '@/Components/Charts/DoughnutChart.vue';
import BarChart from '@/Components/Charts/BarChart.vue';
import LineChart from '@/Components/Charts/LineChart.vue';

const props = defineProps({
    collaboratorsList: {
        type: Array,
        default: () => []
    },
    initialFechaInicio: {
        type: String,
        default: ''
    },
    initialFechaFin: {
        type: String,
        default: ''
    }
});

// Filtros
const fechaInicio = ref(props.initialFechaInicio || new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().substring(0, 10));
const fechaFin = ref(props.initialFechaFin || new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().substring(0, 10));
const searchText = ref('');
const filterStatus = ref('all'); // 'all' | 'favor' | 'deuda' | 'puntual'
const loading = ref(false);
const generalData = ref(null);
const activeTab = ref('empleados'); // 'empleados' | 'graficos'

// Presets de fechas
const applyPreset = (preset) => {
    const now = new Date();
    if (preset === 'mes_actual') {
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        fechaInicio.value = firstDay.toISOString().substring(0, 10);
        fechaFin.value = lastDay.toISOString().substring(0, 10);
    } else if (preset === 'mes_anterior') {
        const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
        fechaInicio.value = firstDay.toISOString().substring(0, 10);
        fechaFin.value = lastDay.toISOString().substring(0, 10);
    } else if (preset === 'ultimos_30') {
        const past = new Date();
        past.setDate(past.getDate() - 30);
        fechaInicio.value = past.toISOString().substring(0, 10);
        fechaFin.value = now.toISOString().substring(0, 10);
    }
    fetchGeneralData();
};

// Consulta asíncrona
const fetchGeneralData = async () => {
    loading.value = true;
    try {
        const response = await axios.post(route('rrhh.asistencia-balance-empleados.data'), {
            fecha_inicio: fechaInicio.value,
            fecha_fin: fechaFin.value
        });
        if (response.data.success) {
            generalData.value = response.data.data;
        }
    } catch (error) {
        console.error('Error al cargar balance general:', error);
    } finally {
        loading.value = false;
    }
};

onMounted(() => {
    fetchGeneralData();
});

// Empleados filtrados por búsqueda y estado
const filteredEmpleados = computed(() => {
    if (!generalData.value || !generalData.value.empleados) return [];

    let list = generalData.value.empleados;

    if (searchText.value.trim()) {
        const query = searchText.value.toLowerCase().trim();
        list = list.filter(e =>
            e.nombre_completo.toLowerCase().includes(query) ||
            (e.num_doc && e.num_doc.includes(query)) ||
            (e.puesto && e.puesto.toLowerCase().includes(query))
        );
    }

    if (filterStatus.value === 'favor') {
        list = list.filter(e => e.saldo_neto > 0);
    } else if (filterStatus.value === 'deuda') {
        list = list.filter(e => e.saldo_neto < 0);
    } else if (filterStatus.value === 'puntual') {
        list = list.filter(e => e.porcentaje_puntualidad >= 90);
    }

    return list;
});

// Exportar a Excel
const exportToExcel = () => {
    if (!generalData.value) return;

    const data = generalData.value;
    const rows = [
        ['INFORME GENERAL DE CONTROL Y BALANCE DE ASISTENCIA DEL PERSONAL'],
        ['Rango de Fechas:', `${data.fechaInicio} al ${data.fechaFin}`],
        ['Total Empleados Procesados:', data.globalTotales.total_colaboradores],
        ['Total Horas Programadas:', data.globalTotales.total_horas_programadas],
        ['Total Horas Laboradas:', data.globalTotales.total_horas_laboradas],
        ['Total Horas Extras (+):', data.globalTotales.total_horas_extra],
        ['Total Horas Debe (-):', data.globalTotales.total_horas_debe],
        ['Saldo Neto Global:', data.globalTotales.saldo_neto_global],
        [],
        [
            'ID', 'Colaborador', 'DNI / Doc', 'Puesto / Cargo', 'Días Prog.', 'Días Asist.',
            'Puntuales', 'Tardanzas', 'Faltas', 'Horas Prog.', 'Horas Lab.', 'Horas Extra (+)',
            'Horas Debe (-)', 'Saldo Neto (hrs)', 'Prom. Ingreso', 'Puntualidad (%)', 'Asistencia (%)'
        ]
    ];

    data.empleados.forEach(e => {
        rows.push([
            e.id,
            e.nombre_completo,
            e.num_doc,
            e.puesto,
            e.dias_programados,
            e.dias_asistidos,
            e.dias_puntuales,
            e.dias_tardanzas,
            e.dias_faltas,
            e.total_horas_programadas,
            e.total_horas_laboradas,
            e.total_horas_extra,
            e.total_horas_debe,
            e.saldo_neto,
            e.promedio_ingreso,
            `${e.porcentaje_puntualidad}%`,
            `${e.porcentaje_asistencia}%`
        ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Balance General');

    const fileName = `Balance_General_Personal_${data.fechaInicio}_${data.fechaFin}.xlsx`;
    XLSX.writeFile(wb, fileName);
};

// Imprimir
const printReport = () => {
    window.print();
};
</script>

<template>
    <Head title="Balance General de Asistencia del Personal - RRHH" />

    <BreezeAuthenticatedLayout>
        <div class="max-w-7xl mx-auto space-y-6 pb-12 print:p-0 print:max-w-none">

            <!-- Encabezado Principal -->
            <div class="print:hidden">
                <PremiumPageHeader
                    title="Balance General de Asistencia del Personal"
                    subtitle="Consolidado de horas trabajadas, horas extras y déficit de asistencia para todo el equipo"
                    icon="tabler:users-group"
                >
                    <template #actions>
                        <Link :href="route('rrhh.asistencia-balance-empleado')"
                              class="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-bold transition-all shadow-xs">
                            <Icon icon="tabler:user-check" class="w-4 h-4 text-indigo-500" />
                            <span>Ver Balance Individual por Empleado</span>
                        </Link>
                    </template>
                </PremiumPageHeader>
            </div>

            <!-- Filtros de Rango de Fechas & Presets -->
            <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm print:hidden space-y-4">
                
                <!-- Quick Presets -->
                <div class="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800/80 pb-4">
                    <div class="flex items-center gap-2">
                        <Icon icon="tabler:adjustments-horizontal" class="w-4 h-4 text-indigo-500" />
                        <span class="text-xs font-bold text-slate-700 dark:text-slate-200">Periodos Rápidos:</span>
                    </div>
                    <div class="flex flex-wrap gap-2">
                        <button type="button" @click="applyPreset('mes_actual')"
                                class="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/50 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 rounded-xl text-xs font-semibold transition-all">
                            Mes Actual
                        </button>
                        <button type="button" @click="applyPreset('mes_anterior')"
                                class="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-semibold transition-all">
                            Mes Anterior
                        </button>
                        <button type="button" @click="applyPreset('ultimos_30')"
                                class="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-xl text-xs font-semibold transition-all">
                            Últimos 30 días
                        </button>
                    </div>
                </div>

                <!-- Date Range Form -->
                <form @submit.prevent="fetchGeneralData" class="grid grid-cols-1 sm:grid-cols-12 gap-4 items-end">
                    
                    <!-- Fecha Inicio -->
                    <div class="sm:col-span-4 space-y-1">
                        <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400">
                            Fecha Inicio
                        </label>
                        <input type="date" v-model="fechaInicio" required
                               class="w-full text-xs px-3.5 py-2.5 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-slate-50/50 dark:bg-slate-850 text-slate-800 dark:text-white font-semibold focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" />
                    </div>

                    <!-- Fecha Fin -->
                    <div class="sm:col-span-4 space-y-1">
                        <label class="block text-[11px] font-bold uppercase tracking-wider text-slate-400">
                            Fecha Fin
                        </label>
                        <input type="date" v-model="fechaFin" required
                               class="w-full text-xs px-3.5 py-2.5 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-slate-50/50 dark:bg-slate-850 text-slate-800 dark:text-white font-semibold focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" />
                    </div>

                    <!-- Botón Procesar -->
                    <div class="sm:col-span-4 flex items-center gap-2">
                        <button type="submit" :disabled="loading"
                                class="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-md shadow-indigo-100 dark:shadow-none transition-all hover:scale-[1.01] active:scale-[0.99]">
                            <Icon :icon="loading ? 'tabler:loader' : 'tabler:refresh'" :class="{ 'animate-spin': loading }" class="w-4 h-4" />
                            <span>{{ loading ? 'Procesando Personal...' : 'Procesar Balance' }}</span>
                        </button>
                    </div>
                </form>
            </div>

            <!-- Loader de estado -->
            <div v-if="loading" class="text-center py-20 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl shadow-sm space-y-3">
                <Icon icon="tabler:loader-2" class="w-12 h-12 text-indigo-600 animate-spin mx-auto" />
                <h4 class="text-sm font-bold text-slate-700 dark:text-slate-200">Consolidando Asistencias del Personal...</h4>
                <p class="text-xs text-slate-400 max-w-sm mx-auto">Calculando horas laboradas, horas extras y faltas de todos los colaboradores.</p>
            </div>

            <!-- Contenido Principal con Datos -->
            <div v-else-if="generalData" class="space-y-6">

                <!-- Header de Acciones de Exportación -->
                <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm">
                    <div class="space-y-0.5">
                        <div class="flex items-center gap-2">
                            <span class="text-[9px] font-mono px-2 py-0.5 bg-indigo-50 dark:bg-indigo-950 text-indigo-600 dark:text-indigo-400 rounded uppercase font-bold">
                                Periodo Consultado
                            </span>
                            <span class="text-xs font-bold text-slate-800 dark:text-white">
                                {{ generalData.fechaInicio }} al {{ generalData.fechaFin }}
                            </span>
                        </div>
                        <p class="text-xs text-slate-400">
                            Total de {{ generalData.globalTotales.total_colaboradores }} colaboradores activos evaluados.
                        </p>
                    </div>

                    <div class="flex flex-wrap items-center gap-2.5 print:hidden">
                        <button type="button" @click="exportToExcel"
                                class="inline-flex items-center gap-2 px-3.5 py-2.5 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:hover:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200/80 dark:border-emerald-800 rounded-xl text-xs font-bold transition-all shadow-xs">
                            <Icon icon="tabler:file-spreadsheet" class="w-4 h-4 text-emerald-600" />
                            <span>Exportar Todo (Excel)</span>
                        </button>
                        <button type="button" @click="printReport"
                                class="inline-flex items-center gap-2 px-3.5 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-bold transition-all shadow-xs">
                            <Icon icon="tabler:printer" class="w-4 h-4 text-slate-600 dark:text-slate-400" />
                            <span>Imprimir / PDF</span>
                        </button>
                    </div>
                </div>

                <!-- Resumen Global de KPIs -->
                <div class="grid grid-cols-2 lg:grid-cols-6 gap-4">
                    
                    <!-- 1. Total Colaboradores -->
                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm space-y-1">
                        <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Personal Total</span>
                        <div class="text-xl font-black text-slate-800 dark:text-white">
                            {{ generalData.globalTotales.total_colaboradores }}
                        </div>
                        <span class="text-[10px] text-slate-400 block">Colaboradores activos</span>
                    </div>

                    <!-- 2. Total Horas Programadas -->
                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm space-y-1">
                        <span class="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Horas Programadas</span>
                        <div class="text-xl font-black text-slate-800 dark:text-white">
                            {{ generalData.globalTotales.total_horas_programadas }} <span class="text-xs font-semibold text-slate-400">h</span>
                        </div>
                        <span class="text-[10px] text-slate-400 block">Jornada institucional</span>
                    </div>

                    <!-- 3. Total Horas Laboradas -->
                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm space-y-1">
                        <span class="text-[10px] font-bold uppercase tracking-wider text-indigo-500 block">Horas Laboradas</span>
                        <div class="text-xl font-black text-indigo-600 dark:text-indigo-400">
                            {{ generalData.globalTotales.total_horas_laboradas }} <span class="text-xs font-semibold text-indigo-400">h</span>
                        </div>
                        <span class="text-[10px] text-slate-400 block">{{ generalData.globalTotales.total_dias_asistidos }} asistencias</span>
                    </div>

                    <!-- 4. Total Horas Extras (+) -->
                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm space-y-1">
                        <span class="text-[10px] font-bold uppercase tracking-wider text-emerald-500 block">Horas Extras (+)</span>
                        <div class="text-xl font-black text-emerald-600 dark:text-emerald-400">
                            +{{ generalData.globalTotales.total_horas_extra }} <span class="text-xs font-semibold text-emerald-400">h</span>
                        </div>
                        <span class="text-[10px] text-emerald-500 font-semibold block">A favor del personal</span>
                    </div>

                    <!-- 5. Total Horas Debe (-) -->
                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm space-y-1">
                        <span class="text-[10px] font-bold uppercase tracking-wider text-rose-500 block">Horas Debe (-)</span>
                        <div class="text-xl font-black text-rose-600 dark:text-rose-400">
                            -{{ generalData.globalTotales.total_horas_debe }} <span class="text-xs font-semibold text-rose-400">h</span>
                        </div>
                        <span class="text-[10px] text-rose-500 font-semibold block">Déficit acumulado</span>
                    </div>

                    <!-- 6. Saldo Neto Global -->
                    <div :class="[
                        generalData.globalTotales.saldo_neto_global >= 0
                            ? 'bg-emerald-50/50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800/80'
                            : 'bg-rose-50/50 border-rose-200 dark:bg-rose-950/20 dark:border-rose-800/80'
                    ]" class="border rounded-2xl p-4 shadow-sm space-y-1">
                        <span class="text-[10px] font-bold uppercase tracking-wider block" :class="generalData.globalTotales.saldo_neto_global >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'">
                            Balance Neto Global
                        </span>
                        <div class="text-xl font-black" :class="generalData.globalTotales.saldo_neto_global >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'">
                            {{ generalData.globalTotales.saldo_neto_global >= 0 ? '+' : '' }}{{ generalData.globalTotales.saldo_neto_global }} <span class="text-xs">h</span>
                        </div>
                        <span class="text-[10px] font-semibold block text-slate-500 dark:text-slate-400">
                            {{ generalData.globalTotales.saldo_neto_global >= 0 ? 'Superávit global' : 'Déficit global' }}
                        </span>
                    </div>

                </div>

                <!-- Tabs de Navegación -->
                <div class="flex border-b border-slate-200 dark:border-slate-800 print:hidden">
                    <button type="button" @click="activeTab = 'empleados'"
                            :class="[
                                activeTab === 'empleados'
                                    ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 font-bold border-b-2'
                                    : 'text-slate-400 hover:text-slate-600 font-medium'
                            ]"
                            class="px-5 py-3 text-xs flex items-center gap-2 transition-all">
                        <Icon icon="tabler:list-details" class="w-4 h-4" />
                        <span>Tabla Consolidada de Colaboradores ({{ filteredEmpleados.length }})</span>
                    </button>
                    <button type="button" @click="activeTab = 'graficos'"
                            :class="[
                                activeTab === 'graficos'
                                    ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 font-bold border-b-2'
                                    : 'text-slate-400 hover:text-slate-600 font-medium'
                            ]"
                            class="px-5 py-3 text-xs flex items-center gap-2 transition-all">
                        <Icon icon="tabler:chart-bar" class="w-4 h-4" />
                        <span>Métricas y Gráficos Estadísticos Globales</span>
                    </button>
                </div>

                <!-- TAB 1: TABLA DE COLABORADORES -->
                <div v-show="activeTab === 'empleados' || activeTab === 'all'" class="space-y-4">
                    
                    <!-- Barra de Búsqueda y Filtros de Estado -->
                    <div class="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-sm print:hidden">
                        <div class="relative w-full sm:w-80">
                            <Icon icon="tabler:search" class="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                            <input type="text" v-model="searchText" placeholder="Buscar por nombre, DNI o cargo..."
                                   class="w-full text-xs pl-9 pr-4 py-2 border border-slate-200 dark:border-slate-700/80 rounded-xl bg-slate-50/50 dark:bg-slate-850 text-slate-800 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all" />
                        </div>

                        <div class="flex items-center gap-2 w-full sm:w-auto overflow-x-auto">
                            <button type="button" @click="filterStatus = 'all'"
                                    :class="filterStatus === 'all' ? 'bg-indigo-600 text-white font-bold' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'"
                                    class="px-3 py-1.5 rounded-xl text-xs transition-all shrink-0">
                                Todos ({{ generalData.empleados.length }})
                            </button>
                            <button type="button" @click="filterStatus = 'favor'"
                                    :class="filterStatus === 'favor' ? 'bg-emerald-600 text-white font-bold' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'"
                                    class="px-3 py-1.5 rounded-xl text-xs transition-all shrink-0">
                                Con Horas a Favor
                            </button>
                            <button type="button" @click="filterStatus = 'deuda'"
                                    :class="filterStatus === 'deuda' ? 'bg-rose-600 text-white font-bold' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'"
                                    class="px-3 py-1.5 rounded-xl text-xs transition-all shrink-0">
                                Con Deuda de Horas
                            </button>
                            <button type="button" @click="filterStatus = 'puntual'"
                                    :class="filterStatus === 'puntual' ? 'bg-indigo-600 text-white font-bold' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'"
                                    class="px-3 py-1.5 rounded-xl text-xs transition-all shrink-0">
                                Alta Puntualidad (≥90%)
                            </button>
                        </div>
                    </div>

                    <!-- Tabla -->
                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl overflow-hidden shadow-sm">
                        <div class="overflow-x-auto">
                            <table class="w-full text-left border-collapse text-xs">
                                <thead>
                                    <tr class="bg-slate-50/80 dark:bg-slate-850 border-b border-slate-100 dark:border-slate-800 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                        <th class="px-4 py-3.5">Colaborador</th>
                                        <th class="px-3 py-3.5 text-center">Días Asist.</th>
                                        <th class="px-3 py-3.5 text-center">Horas Prog.</th>
                                        <th class="px-3 py-3.5 text-center">Horas Lab.</th>
                                        <th class="px-3 py-3.5 text-center">Extras (+)</th>
                                        <th class="px-3 py-3.5 text-center">Debe (-)</th>
                                        <th class="px-3 py-3.5 text-center">Balance Neto</th>
                                        <th class="px-3 py-3.5 text-center">Prom. Ingreso</th>
                                        <th class="px-3 py-3.5 text-center">Puntualidad</th>
                                        <th class="px-4 py-3.5 text-center print:hidden">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody class="divide-y divide-slate-50 dark:divide-slate-800/60">
                                    <tr v-for="emp in filteredEmpleados" :key="emp.id"
                                        class="hover:bg-slate-50/50 dark:hover:bg-slate-850/40 transition-colors">
                                        
                                        <!-- Nombre y Cargo -->
                                        <td class="px-4 py-3">
                                            <div class="font-bold text-slate-800 dark:text-white">
                                                {{ emp.nombre_completo }}
                                            </div>
                                            <div class="flex items-center gap-2 text-[10px] text-slate-400 mt-0.5">
                                                <span class="font-mono">{{ emp.num_doc }}</span>
                                                <span>•</span>
                                                <span>{{ emp.puesto }}</span>
                                            </div>
                                        </td>

                                        <!-- Días Asistidos -->
                                        <td class="px-3 py-3 text-center font-semibold text-slate-700 dark:text-slate-300">
                                            <span>{{ emp.dias_asistidos }}</span>
                                            <span class="text-slate-400 text-[10px]"> / {{ emp.dias_programados }}</span>
                                        </td>

                                        <!-- Horas Programadas -->
                                        <td class="px-3 py-3 text-center font-mono text-slate-500">
                                            {{ emp.total_horas_programadas }}h
                                        </td>

                                        <!-- Horas Laboradas -->
                                        <td class="px-3 py-3 text-center font-mono font-extrabold text-indigo-600 dark:text-indigo-400">
                                            {{ emp.total_horas_laboradas }}h
                                        </td>

                                        <!-- Extras (+) -->
                                        <td class="px-3 py-3 text-center font-mono font-bold text-emerald-600">
                                            {{ emp.total_horas_extra > 0 ? `+${emp.total_horas_extra}h` : '-' }}
                                        </td>

                                        <!-- Debe (-) -->
                                        <td class="px-3 py-3 text-center font-mono font-bold text-rose-500">
                                            {{ emp.total_horas_debe > 0 ? `-${emp.total_horas_debe}h` : '-' }}
                                        </td>

                                        <!-- Balance Neto -->
                                        <td class="px-3 py-3 text-center">
                                            <span :class="[
                                                emp.saldo_neto >= 0
                                                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border-emerald-200'
                                                    : 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 border-rose-200'
                                            ]" class="px-2 py-0.5 rounded-lg border font-mono font-bold text-[11px]">
                                                {{ emp.saldo_neto >= 0 ? '+' : '' }}{{ emp.saldo_neto }}h
                                            </span>
                                        </td>

                                        <!-- Promedio Ingreso -->
                                        <td class="px-3 py-3 text-center font-mono font-bold text-slate-700 dark:text-slate-300">
                                            {{ emp.promedio_ingreso }}
                                        </td>

                                        <!-- Puntualidad (%) -->
                                        <td class="px-3 py-3 text-center">
                                            <span :class="[
                                                emp.porcentaje_puntualidad >= 90 ? 'text-emerald-600 font-extrabold' :
                                                emp.porcentaje_puntualidad >= 75 ? 'text-amber-500 font-bold' : 'text-rose-500 font-bold'
                                            ]">
                                                {{ emp.porcentaje_puntualidad }}%
                                            </span>
                                        </td>

                                        <!-- Acciones (Ver Ficha Individual) -->
                                        <td class="px-4 py-3 text-center print:hidden">
                                            <Link :href="route('rrhh.asistencia-balance-empleado', { id_collaborator: emp.id })"
                                                  class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 dark:bg-indigo-950/50 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 rounded-xl text-[11px] font-bold transition-all"
                                                  title="Ver Ficha Individual de Asistencia">
                                                <Icon icon="tabler:eye" class="w-3.5 h-3.5" />
                                                <span>Ficha</span>
                                            </Link>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- TAB 2: GRÁFICOS ESTADÍSTICOS GLOBALES -->
                <div v-show="activeTab === 'graficos' || activeTab === 'all'" class="space-y-6">
                    
                    <!-- 1. TENDENCIA DE ASISTENCIA DIARIA (COLABORADORES PRESENTES) - Full Width (Imagen 4) -->
                    <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                        <div class="flex items-center justify-between border-b border-slate-100 dark:border-slate-800/80 pb-3">
                            <h4 class="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200 flex items-center gap-2">
                                <Icon icon="tabler:chart-line" class="w-4 h-4 text-indigo-500" />
                                TENDENCIA DE ASISTENCIA DIARIA (COLABORADORES PRESENTES)
                            </h4>
                        </div>
                        <div class="h-72">
                            <LineChart :data="generalData.charts.tendenciaDiaria" xTitle="Fecha" yTitle="Colaboradores" class="h-full w-full" />
                        </div>
                    </div>

                    <!-- Fila de Gráficos Complementarios -->
                    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        
                        <!-- 2. Top Horas Laboradas por Colaborador -->
                        <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                            <div class="flex items-center justify-between border-b border-slate-100 dark:border-slate-800/80 pb-3">
                                <h4 class="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                                    <Icon icon="tabler:award" class="w-4 h-4 text-indigo-500" />
                                    Top Horas Laboradas
                                </h4>
                            </div>
                            <div class="h-64">
                                <BarChart :data="generalData.charts.topHorasLaboradas" xTitle="Colaborador" yTitle="Horas" class="h-full w-full" />
                            </div>
                        </div>

                        <!-- 3. Extras vs Debe por Colaborador -->
                        <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                            <div class="flex items-center justify-between border-b border-slate-100 dark:border-slate-800/80 pb-3">
                                <h4 class="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                                    <Icon icon="tabler:scale" class="w-4 h-4 text-emerald-500" />
                                    Horas Extras vs Debe
                                </h4>
                            </div>
                            <div class="h-64">
                                <BarChart :data="generalData.charts.extrasVsDebe" :showLegend="true" xTitle="Colaborador" yTitle="Horas" class="h-full w-full" />
                            </div>
                        </div>

                        <!-- 4. Distribución de Puntualidad del Personal -->
                        <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                            <div class="flex items-center justify-between border-b border-slate-100 dark:border-slate-800/80 pb-3">
                                <h4 class="text-xs font-bold uppercase tracking-wider text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                                    <Icon icon="tabler:chart-pie" class="w-4 h-4 text-amber-500" />
                                    Puntualidad Global
                                </h4>
                            </div>
                            <div class="h-64 flex items-center justify-center">
                                <DoughnutChart :data="generalData.charts.distribucionPuntualidad" title="Puntualidad del Personal" />
                            </div>
                        </div>

                    </div>
                </div>

            </div>

        </div>
    </BreezeAuthenticatedLayout>
</template>
